/**
 * V114.3: MM 직원 목록 슬라이드 패널
 *
 * 투입인력/유휴인력/중복투입 카드 클릭 시 표시되는 직원 상세 목록 패널
 *
 * @module pages/mm/components/MmEmployeeListPanel
 */

import React from 'react';
import { X, Users, AlertCircle, Copy } from 'lucide-react';
import { MmEmployeeInfo, MmDuplicatedEmployeeInfo } from '../../../shared/types/mm';

// ============================================
// 패널 타입 정의
// ============================================

export type MmEmployeePanelType = 'assigned' | 'idle' | 'duplicated';

interface MmEmployeeListPanelProps {
  /** 패널 타입 */
  type: MmEmployeePanelType;

  /** 팀명 */
  teamName: string;

  /** 직원 목록 (투입/유휴) */
  employees?: MmEmployeeInfo[];

  /** 중복투입 직원 목록 (프로젝트명 포함) */
  duplicatedEmployees?: MmDuplicatedEmployeeInfo[];

  /**
   * V117.1: 중복투입 직원 ID Set (투입인력 패널에서 배지 표시용)
   *
   * MmTeamSummary.duplicatedEmployees에서 추출한 userId Set
   * 투입인력 패널에서 이 Set에 포함된 인원에게만 "중복투입" 배지 표시
   */
  duplicatedUserIds?: Set<string>;

  /** 패널 닫기 콜백 */
  onClose: () => void;
}

// ============================================
// 헬퍼: 타입별 텍스트/스타일
// ============================================

function getPanelConfig(type: MmEmployeePanelType) {
  switch (type) {
    case 'assigned':
      return {
        title: '투입인력 명단',
        icon: Users,
        headerBg: '#eef2ff', // indigo-50
        headerColor: '#4f46e5', // indigo-600
        emptyText: '투입된 인원이 없습니다.',
      };
    case 'idle':
      return {
        title: '유휴인력 명단',
        icon: AlertCircle,
        headerBg: '#fefce8', // yellow-50
        headerColor: '#ca8a04', // yellow-600
        emptyText: '유휴 인원이 없습니다.',
      };
    case 'duplicated':
      return {
        title: '중복투입 인력 명단',
        icon: Copy,
        headerBg: '#eff6ff', // blue-50
        headerColor: '#2563eb', // blue-600
        emptyText: '중복 투입된 인원이 없습니다.',
      };
  }
}

// ============================================
// 컴포넌트
// ============================================

export function MmEmployeeListPanel({
  type,
  teamName,
  employees,
  duplicatedEmployees,
  duplicatedUserIds,
  onClose,
}: MmEmployeeListPanelProps) {
  const config = getPanelConfig(type);
  const Icon = config.icon;

  // 데이터 선택
  const data = type === 'duplicated' ? duplicatedEmployees : employees;
  const isEmpty = !data || data.length === 0;

  // 테이블 스타일
  const thStyle: React.CSSProperties = {
    padding: '10px 12px',
    textAlign: 'left',
    fontWeight: 600,
    fontSize: '13px',
    color: '#475569',
    background: '#f8fafc',
    borderBottom: '1px solid #e2e8f0',
    whiteSpace: 'nowrap',
  };

  const tdStyle: React.CSSProperties = {
    padding: '10px 12px',
    fontSize: '14px',
    borderBottom: '1px solid #f1f5f9',
    color: '#1e293b',
  };

  return (
    <>
      {/* Backdrop */}
      <div
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,0.3)',
          zIndex: 998,
        }}
        onClick={onClose}
      />

      {/* Panel */}
      <div
        style={{
          position: 'fixed',
          right: 0,
          top: 0,
          bottom: 0,
          width: type === 'duplicated' ? '600px' : '500px',
          background: 'white',
          boxShadow: '-4px 0 20px rgba(0,0,0,0.15)',
          zIndex: 999,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* 헤더 */}
        <div
          style={{
            padding: '16px 20px',
            borderBottom: '1px solid #e2e8f0',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            background: config.headerBg,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <Icon size={20} style={{ color: config.headerColor }} />
            <div>
              <h2
                style={{
                  margin: 0,
                  fontSize: '16px',
                  fontWeight: 600,
                  color: config.headerColor,
                }}
              >
                {config.title}
              </h2>
              <p
                style={{
                  margin: '4px 0 0 0',
                  fontSize: '13px',
                  color: '#64748b',
                }}
              >
                {teamName} · {data?.length || 0}명
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              border: 'none',
              background: 'none',
              cursor: 'pointer',
              padding: '4px',
              borderRadius: '4px',
            }}
          >
            <X size={20} />
          </button>
        </div>

        {/* 본문 */}
        <div style={{ flex: 1, overflow: 'auto', padding: '16px 20px' }}>
          {isEmpty ? (
            <div
              style={{
                textAlign: 'center',
                padding: '40px 20px',
                color: '#64748b',
              }}
            >
              {config.emptyText}
            </div>
          ) : type === 'duplicated' && duplicatedEmployees ? (
            // 중복투입 테이블 (프로젝트명 컬럼 포함)
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={thStyle}>소속</th>
                  <th style={thStyle}>직책</th>
                  <th style={thStyle}>직위</th>
                  <th style={thStyle}>이름</th>
                  <th style={thStyle}>투입 프로젝트</th>
                </tr>
              </thead>
              <tbody>
                {duplicatedEmployees.map((emp) => (
                  <tr key={emp.userId}>
                    <td style={tdStyle}>{emp.orgUnitName || '-'}</td>
                    <td style={tdStyle}>{emp.jobTitle || '-'}</td>
                    <td style={tdStyle}>{emp.positionTitle || '-'}</td>
                    <td style={{ ...tdStyle, fontWeight: 500 }}>{emp.name}</td>
                    <td style={{ ...tdStyle, maxWidth: '180px' }}>
                      <div
                        style={{
                          display: 'flex',
                          flexWrap: 'wrap',
                          gap: '4px',
                        }}
                      >
                        {emp.projectNames.map((pn, idx) => (
                          <span
                            key={idx}
                            style={{
                              padding: '2px 8px',
                              background: '#e0e7ff',
                              color: '#4338ca',
                              borderRadius: '4px',
                              fontSize: '12px',
                            }}
                          >
                            {pn}
                          </span>
                        ))}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : employees ? (
            // 투입/유휴 테이블
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={thStyle}>소속</th>
                  <th style={thStyle}>직책</th>
                  <th style={thStyle}>직위</th>
                  <th style={thStyle}>이름</th>
                </tr>
              </thead>
              <tbody>
                {employees.map((emp) => {
                  // V69 Phase 2: 외부(기타) 인력 분기 — [기타] {externalOrgName} / {externalName}
                  //   클릭 핸들러 없음 (행 자체에 onClick 미부여) → 직원 상세 사이드패널 미오픈 보장.
                  //   행 스타일: 회색 톤 (점선 테두리 없이 배경만 변경) — 내부 직원과 시각적 구분.
                  if (emp.isExternal === true) {
                    return (
                      <tr
                        key={emp.userId}
                        style={{ backgroundColor: 'var(--color-gray-50, #f9fafb)' }}
                      >
                        <td style={tdStyle}>
                          <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <span
                              style={{
                                padding: '2px 6px',
                                background: '#e5e7eb',
                                color: '#374151',
                                borderRadius: '4px',
                                fontSize: '11px',
                                fontWeight: 600,
                              }}
                            >
                              기타
                            </span>
                            {emp.externalOrgName || '-'}
                          </span>
                        </td>
                        <td style={tdStyle}>-</td>
                        <td style={tdStyle}>-</td>
                        <td style={{ ...tdStyle, fontWeight: 500 }}>
                          {emp.externalName || emp.name || '-'}
                        </td>
                      </tr>
                    );
                  }

                  return (
                    <tr key={emp.userId}>
                      <td style={tdStyle}>{emp.orgUnitName || '-'}</td>
                      <td style={tdStyle}>{emp.jobTitle || '-'}</td>
                      <td style={tdStyle}>{emp.positionTitle || '-'}</td>
                      <td style={{ ...tdStyle, fontWeight: 500 }}>
                        <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          {emp.name}
                          {/* V117.2: 중복투입 배지 (팀 소속 + 이 팀 프로젝트 2개 이상 투입 시) */}
                          {type === 'assigned' &&
                            emp.isTeamMember === true &&
                            (emp.teamProjectSlotCount ?? 0) >= 2 && (
                              <span
                                style={{
                                  padding: '2px 6px',
                                  background: '#dbeafe', // blue-100
                                  color: '#1e40af', // blue-800
                                  borderRadius: '4px',
                                  fontSize: '11px',
                                  fontWeight: 500,
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                중복투입 x{emp.teamProjectSlotCount}
                              </span>
                            )}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          ) : null}
        </div>
      </div>
    </>
  );
}
